// ----------------------------------------------------------------------------
// Join with an invite code. /invite/:code is a deep link that pre-fills the
// code; the person confirms, we join (church code first, then team code, as
// before), and they wait for an admin to approve. No invite links or email.
// ----------------------------------------------------------------------------

import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Check, Clock } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { churchesApi, groupsApi } from '../api/organization';
import { ChurchMembershipState, type Membership } from '../types';
import { AuthCard, AuthScreen, AuthTitle } from '../components/auth/AuthScreen';
import { apiErrorMessage } from '@app/lib/apiErrors';
import { Button, Field, Input, SkeletonRows } from '../components/ui';

type Step = 'enter' | 'pending' | 'joined';

export const AcceptInvite: React.FC = () => {
  const { code: codeParam } = useParams<{ code: string }>();
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState((codeParam || '').toUpperCase());
  const [step, setStep] = useState<Step>('enter');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [shake, setShake] = useState(0);

  const join = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = code.trim();
    if (trimmed.replace(/[^A-Z0-9]/gi, '').length < 6) {
      setError('Enter the full code, for example GRACE-7K2Q.');
      setShake((n) => n + 1);
      return;
    }
    if (!user) return;
    setBusy(true);
    setError('');
    try {
      // Try church-level invite code first, then group-level
      let membership: Membership;
      try {
        membership = await churchesApi.joinByInviteCode(trimmed, user.id);
      } catch {
        membership = await groupsApi.joinByCode(trimmed, user.id);
      }
      setStep(membership?.churchState === ChurchMembershipState.Active ? 'joined' : 'pending');
    } catch (err: any) {
      setError(apiErrorMessage(err, 'That code didn’t match a church. It may have expired; check it with your admin.', 'member'));
      setShake((n) => n + 1);
    } finally {
      setBusy(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center p-4">
        <SkeletonRows rows={2} className="w-72" />
      </div>
    );
  }

  if (!user) {
    const back = `/invite/${encodeURIComponent(codeParam || '')}`;
    return (
      <AuthScreen>
        <AuthTitle title="Join your church" sub="Log in or create an account, then we'll use your invite code." />
        <AuthCard className="p-[26px] flex flex-col gap-4">
          <div className="bg-fill rounded-[12px] px-3.5 py-3">
            <div className="text-[12px] text-t4">Invite code</div>
            <div className="font-mono text-[18px] font-bold tracking-[.12em] mt-0.5">{(codeParam || '').toUpperCase()}</div>
          </div>
          <Button size="xl" shape="form" block glow onClick={() => navigate(`/login?redirect=${encodeURIComponent(back)}`)}>
            Log in to join
          </Button>
          <p className="text-[12.5px] text-t4 text-center">Don't have an account? You can create one from the log-in page.</p>
        </AuthCard>
      </AuthScreen>
    );
  }

  if (step !== 'enter') {
    const pending = step === 'pending';
    return (
      <AuthScreen>
        <AuthCard className="px-[26px] py-[34px] text-center">
          <div
            className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-[18px] ${pending ? 'bg-info-bg text-info' : 'bg-ok-bg text-ok'}`}
          >
            {pending ? <Clock size={26} strokeWidth={1.8} aria-hidden="true" /> : <Check size={26} strokeWidth={2.2} aria-hidden="true" />}
          </div>
          <h1 className="text-[22px] font-semibold tracking-[-0.02em]" data-testid={pending ? 'pending-approval' : undefined}>
            {pending ? 'Request sent' : "You're in"}
          </h1>
          <p className="text-[14px] text-t3 mt-2 leading-[1.55]">
            {pending
              ? 'An admin at your church will review it. Only an admin can approve you.'
              : 'You have joined the church.'}
          </p>
          <Button variant="dark" size="lg" shape="form" block className="mt-[22px]" onClick={() => navigate('/upload')}>
            Continue
          </Button>
        </AuthCard>
      </AuthScreen>
    );
  }

  return (
    <AuthScreen>
      <AuthTitle title="Join your church" sub="Enter the invite code your church admin shared with you." />
      <AuthCard as="form" onSubmit={join} noValidate shake={shake} className="p-[26px] flex flex-col gap-4">
        <Field label="Invite code" htmlFor="invite-code" error={error}>
          <Input
            id="invite-code"
            height={56}
            mono
            value={code}
            onChange={(e) => {
              setCode(e.target.value.toUpperCase());
              setError('');
            }}
            placeholder="GRACE-7K2Q"
            autoComplete="off"
            invalid={!!error}
            className="!text-[20px] font-bold tracking-[.12em] uppercase"
          />
        </Field>
        <Button type="submit" size="xl" shape="form" block glow loading={busy}>
          Request to join
        </Button>
        <p className="text-[12.5px] leading-[1.55] text-t4 text-center">
          Don't have a code? Ask an admin at your church to generate one.
        </p>
      </AuthCard>
      <Link to="/upload" className="mt-6 text-[13.5px]">
        ← Back
      </Link>
    </AuthScreen>
  );
};
